import api from '@/lib/api';
import { API_ENDPOINTS } from '@/constants/endpoints';
import type { ApiResponse } from '@/types';
import type { TranscriptMessage } from '@/modules/simulator/conversations/conversation-parts';

export type ThreadSource = 'ticket' | 'conversation';
export type ThreadRole = 'customer' | 'agent' | 'bot' | 'system';

export interface ThreadMessage {
	role: ThreadRole;
	text: string;
	createdAt: string | null;
}

export interface TicketThread {
	source: ThreadSource;
	externalTicketId: string;
	subject: string | null;
	conversationId: number | null;
	customer: { name: string | null; email: string | null } | null;
	resolvedAt: string | null;
	/** First human agent message; a past scenario hides it and compares against it. */
	firstAgentReplyIndex: number | null;
	messages: ThreadMessage[];
}

export const ticketService = {
	/** Null when no store knows the ticket; a 404 is an answer here, not an error. */
	async getThread(externalTicketId: string): Promise<TicketThread | null> {
		const response = await api.get<ApiResponse<TicketThread>>(
			API_ENDPOINTS.TICKETS.THREAD(encodeURIComponent(externalTicketId)),
			{ validateStatus: (status) => status === 200 || status === 404 }
		);
		return response.status === 404 ? null : response.data.data;
	}
};

const TRANSCRIPT_ROLE: Partial<Record<ThreadRole, TranscriptMessage['role']>> = {
	customer: 'user',
	agent: 'agent',
	bot: 'assistant'
};

/** System rows are events, not turns, so they are left out. */
export const threadToTranscript = (thread: TicketThread): TranscriptMessage[] =>
	thread.messages.flatMap((m) => {
		const role = TRANSCRIPT_ROLE[m.role];
		return role ? [{ role, content: m.text, createdAt: m.createdAt ?? '' }] : [];
	});
